// Was ein Werk an eingelesenen Dateien trägt — die Teile, die für JEDEN Werktyp gleich aussehen.
//
// ⚠️ Diese Datei ist eine EXTRAKTION, kein Neubau: die vier Blöcke unten standen bis zum
// 2026-09-01 inline im `AudioForm`. Als das MindBook-Formular dieselbe Fläche brauchte, gab es
// nichts zu konsumieren — die eigenständige `Fassungsliste` war in #750 in die Audio-Zeilen
// aufgegangen (Toms „eine Fläche statt drei"), und was blieb, hing entweder an den vier
// Varianten-Schaltern (`SchalterDeckung`) oder klebte im Formular fest.
//
// Der zweite Ort hätte die Wahl gelassen: abschreiben oder herausziehen. Abschreiben wäre der
// Anfang genau der Klasse gewesen, die dieses Projekt mehrfach teuer bezahlt hat (fünf Handkopien
// der Varianten-Namen, zwei der Beschreibungs-Reihenfolge — beide waren auseinandergelaufen,
// bevor es jemandem auffiel).
//
// ── Was hier NICHT hineingehört ────────────────────────────────────────────────────────────────
// Die Deckungszeile JE SCHALTER (`SchalterDeckung`). Sie hängt an den vier Varianten-Slots, und
// die hat nur die Audio-Welt: ein MindBook führt zwei Fassungen (mit/ohne Subliminals) über URLs,
// keine Slots. Sie bleibt deshalb dort, wo die Schalter sind.
import React from 'react';
import { formatiereDateigroesse } from '../../utils/dateigroesse';
import { GRUNDFASSUNG, ROLLE_ANZEIGE } from '../../types/fassungen';
import { formatMsToTimecode } from '../../utils/markersParser';
import type { FassungsZustand } from '../../hooks/useFassungen';

interface FassungsUebersichtProps {
  /** Der geteilte Zustand aus `useFassungen`. */
  zustand: FassungsZustand;
  /**
   * `true` in Kontexten OHNE Varianten-Schalter (MindBook): dann listet diese Fläche die
   * Ton-Dateien selbst auf, weil sie sonst nirgends erschienen.
   *
   * ⚠️ Im Audio-Formular bleibt das `false` — dort steht jede Ton-Datei bereits an ihrem Schalter,
   * und eine zweite Liste daneben zeigte dasselbe ein zweites Mal. Genau das war Toms Befund
   * („eine Fläche, nicht drei"), und er soll nicht durch die Hintertür zurückkommen.
   */
  mitTonListe?: boolean;
  /**
   * Wie eine Fassung in DIESEM Kontext heißt — Schlüssel ist der Fassungswert,
   * `GRUNDFASSUNG` steht für `fassung: null`.
   *
   * ⚠️ Warum der Aufrufer die Worte mitgibt und die Fläche nicht selbst nach Werktyp entscheidet:
   * sie kennt keine Werktypen und soll auch keine kennen. Ein `werktyp`-Schalter hier hieße, dass
   * jede neue Werkart diese Datei anfasst. So bringt jeder Ort seine Sprache mit — und die
   * MindBook-Sprache steht in `types/fassungen`, damit Formularfelder und Liste dieselbe benutzen.
   *
   * Ohne Angabe bleibt es bei der Audio-Welt-Sprache („Grundfassung", roher Fassungsschlüssel).
   */
  etiketten?: Record<string, string>;
}

/**
 * Was in einer Kapitel-Zeile vor dem Dateinamen steht: Titel und Zeitspanne.
 *
 * ⚠️ „bis Ende" statt eines Strichs, wenn `dauerMs` fehlt: beim LETZTEN Kapitel ist die Dauer
 * bewusst `null` — es spielt bis zum Datei-Ende. Das ist eine Aussage, kein Loch. Ein „–" oder
 * eine graue Lücke sähe aus, als hätte der Ingest etwas verschluckt, und schickte jemanden
 * suchen.
 *
 * ⛔ Hier wird NICHTS aufsummiert. Die Zeitspanne kommt aus `startMs`/`dauerMs` des EINEN
 * Kapitels; eine Gesamtdauer aus den Kapiteln zu rechnen unterschlägt genau die fehlende letzte
 * (🌐 hat es gemessen: 95 statt 115 Minuten).
 */
const kapitelZeitspanne = (startMs: number | null, dauerMs: number | null): string | null => {
  if (startMs === null) return null;
  const start = formatMsToTimecode(startMs);
  if (dauerMs === null) return `ab ${start} · bis Ende`;
  return `${start}–${formatMsToTimecode(startMs + dauerMs)} · ${formatMsToTimecode(dauerMs)}`;
};

/** Eine Datei-Zeile: Fassung · Dateiname · Größe · Lieferart · Zugriff. */
const DateiZeile: React.FC<{
  fassung: string;
  dateiname: string;
  groesseBytes: number | null;
  oeffentlich: boolean;
  zone: string | null;
}> = ({ fassung, dateiname, groesseBytes, oeffentlich, zone }) => (
  <li className="break-words text-sm text-secondary-600">
    <span className="font-medium text-secondary-800">{fassung}</span>
    {' · '}
    {dateiname}
    {groesseBytes !== null && ` · ${formatiereDateigroesse(groesseBytes)}`}
    {/* Angezeigt wird die WIRKUNG, nicht der Zonenname — der ist ein Betriebsname und steht als
        Titel daneben. */}
    <span
      className={`badge ml-2 ${oeffentlich ? 'badge-secondary' : 'badge-primary'}`}
      title={zone ?? undefined}
    >
      {oeffentlich ? 'öffentlich' : 'nur mit Token'}
    </span>
  </li>
);

export const FassungsUebersicht: React.FC<FassungsUebersichtProps> = ({
  zustand,
  mitTonListe = false,
  etiketten,
}) => {
  /** Der Anzeigename einer Fassung — Kontext-Etikett, sonst der rohe Wert. */
  const etikett = (fassung: string | null): string => {
    const schluessel = fassung ?? GRUNDFASSUNG;
    return etiketten?.[schluessel] ?? schluessel;
  };

  const { uebersicht, laedt, fehler, erneut } = zustand;

  if (laedt) return <div className="skeleton h-5 w-64" aria-hidden="true"></div>;

  // Auf dem Fehlerpfad wird NICHTS über Dateien behauptet — der Grund steht einmal da, mit
  // Wiederholen-Knopf. Ein 403 und „keine Dateien" sehen sonst gleich aus und sind gegenteilige
  // Aussagen.
  if (fehler) {
    return (
      <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-3">
        <p className="max-w-prose text-sm text-secondary-600">{fehler}</p>
        <button type="button" onClick={erneut} className="mt-2 text-sm font-medium text-primary-700 underline">
          Erneut versuchen
        </button>
      </div>
    );
  }

  if (!uebersicht) return null;

  const tonDateien = uebersicht.eintraege.filter((e) => e.rolle === 'audio');
  // ⚠️ Ein Kapitel erkennt man am `teil`, nicht am Werktyp: das Merkmal steht in den Daten. Ein
  // MindBook ohne Kapitel-CSV hat keines, ein mehrteiliges Audio-Werk könnte welche haben.
  const kapitel = tonDateien
    .filter((e) => e.teil !== null)
    .sort((a, b) => (a.teil ?? 0) - (b.teil ?? 0));
  const ohneKapitel = tonDateien.filter((e) => e.teil === null);
  const uebrige = uebersicht.eintraege.filter((e) => e.rolle !== 'audio');

  return (
    <div className="space-y-3">
      {uebersicht.anzahl === 0 && (
        <p className="max-w-prose text-sm text-secondary-500">
          Für dieses Werk sind keine Dateien eingelesen. Dateien entstehen beim Ingest-Lauf, nicht
          beim Speichern dieses Formulars.
        </p>
      )}

      {/* ── Nicht zugeordnete Ton-Dateien ──────────────────────────────────────────────────────
          ⚠️ BENENNEND, nicht als Fehler gefärbt. Bei MindBooks steht hier planmäßig `hoerbuch`:
          dessen Fassungswechsel läuft über eine URL und nicht über einen Schalter — das ist eine
          Entscheidung des Vokabulars, kein Defekt. Ein roter Kasten machte daraus einen Fehler,
          den niemand beheben kann, und wer ihn zweimal gesehen hat, sieht ihn nicht mehr.
          Der Satz darunter sagt deshalb, was Sache ist: die Dateien sind da. */}
      {uebersicht.nichtZugeordnet.length > 0 && (
        <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-3">
          <p className="max-w-prose text-sm font-medium text-secondary-800">
            {uebersicht.nichtZugeordnet.length === 1
              ? 'Eine eingelesene Ton-Datei hängt an keinem Schalter:'
              : `${uebersicht.nichtZugeordnet.length} eingelesene Ton-Dateien hängen an keinem Schalter:`}
          </p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-secondary-700">
            {uebersicht.nichtZugeordnet.map((e) => (
              <li key={e.dateiname}>
                <span className="font-medium">{e.fassung ? etikett(e.fassung) : '—'}</span> ·{' '}
                {e.dateiname}
              </li>
            ))}
          </ul>
          <p className="mt-1 max-w-prose text-sm text-secondary-600">
            Die Dateien sind vorhanden und werden ausgeliefert — sie werden nur über keinen der
            Schalter dieses Formulars gesteuert.
          </p>
        </div>
      )}

      {/* Die Hinweise des Backends — darunter der doppelt belegte Schalter. */}
      {uebersicht.hinweise.length > 0 && (
        <ul className="max-w-prose list-disc space-y-1 pl-5 text-sm text-secondary-600">
          {uebersicht.hinweise.map((h) => (
            <li key={h}>{h}</li>
          ))}
        </ul>
      )}

      {/* Die Ton-Dateien selbst — NUR wo es keine Schalter gibt, siehe `mitTonListe`. */}
      {mitTonListe && tonDateien.length > 0 && (
        <div className="rounded-lg border border-secondary-200 p-4">
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-secondary-500">
            Eingelesene Ton-Dateien
          </h3>
          <ul className="space-y-1">
            {/* ⚠️ Kapitel ZUERST und nach `teil` geordnet, nicht in Ingest-Reihenfolge: acht
                Zeilen, die alle „Grundfassung · audio.mp3" hießen, waren genau Toms Befund — sie
                unterschieden sich nur in der Dateigröße. Der Titel führt jetzt, weil er das
                Einzige ist, woran ein Mensch ein Kapitel wiedererkennt.
                Die Sortierung passiert HIER und nicht im Backend: das DTO gibt die Daten, die
                Reihenfolge ist eine Anzeige-Entscheidung. */}
            {kapitel.map((e) => {
              const spanne = kapitelZeitspanne(e.startMs, e.dauerMs);
              return (
                // ⚠️ Der Schlüssel trägt `teil` MIT: Toms Befund war, dass alle acht Kapitel
                // „audio.mp3" heißen — sie können sich auch im PFAD gleichen, wenn sie in einem
                // Werkordner liegen. Ein Schlüssel aus dem Pfad allein kollidierte dann, und React
                // zeigte stillschweigend weniger Zeilen als vorhanden.
                <li key={`${e.pfad}#${e.teil}`} className="break-words text-sm text-secondary-600">
                  <span className="font-medium text-secondary-800">
                    {e.titel ?? `Kapitel ${e.teil}`}
                  </span>
                  {spanne && <span className="text-secondary-500">{' · '}{spanne}</span>}
                  {' · '}
                  {e.dateiname}
                  {e.groesseBytes !== null && ` · ${formatiereDateigroesse(e.groesseBytes)}`}
                  <span
                    className={`badge ml-2 ${e.oeffentlich ? 'badge-secondary' : 'badge-primary'}`}
                    title={e.zone ?? undefined}
                  >
                    {e.oeffentlich ? 'öffentlich' : 'nur mit Token'}
                  </span>
                </li>
              );
            })}
            {/* Alles ohne `teil` — unverändert wie zuvor. */}
            {ohneKapitel.map((e) => (
              <DateiZeile
                key={e.pfad}
                fassung={etikett(e.fassung)}
                dateiname={e.dateiname}
                groesseBytes={e.groesseBytes}
                oeffentlich={e.oeffentlich}
                zone={e.zone}
              />
            ))}
          </ul>
        </div>
      )}

      {/* ── Der Fuß: was zu keinem Schalter gehört, aber zum Werk ───────────────────────────────
          Titelbild, Transkript, … ⚠️ Kein Kasten und ein anderer RANG als die Zeilen darüber —
          💎-Befund 1 vom 2026-09-01: mit gleichem Rahmen und byte-identischer Überschrift las sich
          der Fuß als eine weitere Variante. Trennlinie und kleine Versalzeile statt Kasten. */}
      {uebrige.length > 0 && (
        <div className="border-t border-secondary-200 pt-4">
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-secondary-500">
            Weitere Dateien dieses Werks
          </h3>
          <ul className="space-y-1">
            {uebrige.map((e) => (
              <DateiZeile
                key={e.pfad}
                fassung={ROLLE_ANZEIGE[e.rolle] ?? e.rolle}
                dateiname={e.dateiname}
                groesseBytes={e.groesseBytes}
                oeffentlich={e.oeffentlich}
                zone={e.zone}
              />
            ))}
          </ul>
        </div>
      )}

      {uebersicht.ordner && (
        <p className="max-w-prose text-xs text-secondary-500">
          Werkordner im Speicher: <code>{uebersicht.ordner}</code>. Zum Abspielen holt die App die
          Adresse jedes Mal neu — sie läuft nach zwölf Stunden ab und steht deshalb bewusst nicht
          hier.
        </p>
      )}
    </div>
  );
};
